import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowLeft, ShieldCheck, AlertTriangle, Gauge } from "lucide-react";
import { api } from "@/lib/api";
import Logo from "@/components/Logo";

export default function BlogPost() {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/blog/posts/${slug}`);
                setPost(data);
            } catch {
                setNotFound(true);
            } finally {
                setLoading(false);
            }
        })();
    }, [slug]);

    if (loading) {
        return (
            <div className="min-h-screen bg-onyx-950 text-white p-8">
                <div className="max-w-3xl mx-auto space-y-4">
                    <div className="h-12 brain-panel animate-pulse" />
                    <div className="h-8 brain-panel animate-pulse w-3/4" />
                    <div className="h-64 brain-panel animate-pulse mt-8" />
                </div>
            </div>
        );
    }

    if (notFound || !post) {
        return (
            <div className="min-h-screen bg-onyx-950 text-white flex flex-col items-center justify-center p-8">
                <Gauge className="h-16 w-16 text-hellcat text-glow-red" />
                <h1
                    className="text-3xl mt-4"
                    style={{ fontFamily: "'Alfa Slab One', 'Ultra', serif", fontWeight: 400 }}
                >
                    Post not found
                </h1>
                <Link
                    to="/the-ladder"
                    className="mt-6 text-hellcat label-caps hover:underline"
                    data-testid="blog-post-back-to-ladder"
                >
                    ← Back to The Ladder
                </Link>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-onyx-950 text-white brain-noise">
            <Helmet>
                <title>{post.title} · The Ladder</title>
                <meta name="description" content={post.excerpt || ""} />
            </Helmet>

            <header
                className="border-b border-onyx-700 sticky top-0 z-40 backdrop-blur-md"
                style={{ background: "rgba(5,5,5,0.85)" }}
            >
                <div className="max-w-6xl mx-auto px-5 py-3 flex items-center justify-between">
                    <Logo size="sm" />
                    <Link
                        to="/the-ladder"
                        className="label-caps text-onyx-500 hover:text-hellcat flex items-center gap-1 transition-colors"
                        data-testid="blog-post-back"
                    >
                        <ArrowLeft className="h-4 w-4" /> The Ladder
                    </Link>
                </div>
            </header>

            <motion.article
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="max-w-3xl mx-auto px-5 py-14"
            >
                <div className="flex items-center gap-2 label-caps mb-4 text-hellcat">
                    <span>{(post.bucket || "").replace(/_/g, " ")}</span>
                    {post.is_real_testimonial && (
                        <span className="brain-mono text-[10px] px-2 py-0.5 bg-emerald-500/15 text-emerald-300 normal-case tracking-normal border border-emerald-500/40">
                            REAL OWNER · VERIFIED
                        </span>
                    )}
                </div>

                <h1
                    className="text-4xl md:text-6xl leading-[1.02] text-white text-glow-red"
                    style={{ fontFamily: "'Alfa Slab One', 'Ultra', serif", fontWeight: 400 }}
                    data-testid="blog-post-title"
                >
                    {post.title}
                </h1>

                <div className="mt-6 flex items-center gap-3 label-caps">
                    <div className="flex items-center gap-2">
                        <div className="h-9 w-9 rounded-full bg-hellcat text-white flex items-center justify-center brain-mono font-bold text-lg shadow-red-glow">
                            R
                        </div>
                        <span className="text-white normal-case tracking-normal text-sm">By {post.author || "Remi"}</span>
                    </div>
                    <span className="text-onyx-600">·</span>
                    <span className="text-onyx-500 brain-mono normal-case tabular-nums">
                        {new Date(post.published_at || Date.now()).toLocaleDateString()}
                    </span>
                </div>

                {/* Disclosures — brutalist warning strips */}
                {post.illustrative && (
                    <div
                        className="mt-8 flex gap-3 border-l-4 border-yellow-500 bg-yellow-500/10 p-4"
                        data-testid="ftc-illustrative"
                    >
                        <AlertTriangle className="h-5 w-5 flex-shrink-0 text-yellow-500 mt-0.5" />
                        <div>
                            <div className="brain-mono text-[10px] uppercase tracking-widest text-yellow-500">
                                // FTC DISCLOSURE · ILLUSTRATIVE
                            </div>
                            <p className="mt-1 text-sm text-yellow-100/90">
                                Composite teaching example written by AI. Names, details, and specifics are fictional to
                                protect owner privacy. Not a testimonial.
                            </p>
                        </div>
                    </div>
                )}
                {post.is_real_testimonial && (
                    <div
                        className="mt-8 flex gap-3 border-l-4 border-emerald-500 bg-emerald-500/10 p-4"
                        data-testid="ftc-real"
                    >
                        <ShieldCheck className="h-5 w-5 flex-shrink-0 text-emerald-400 mt-0.5" />
                        <div>
                            <div className="brain-mono text-[10px] uppercase tracking-widest text-emerald-400">
                                // FTC DISCLOSURE · REAL OWNER · VERIFIED
                            </div>
                            <p className="mt-1 text-sm text-emerald-100/90">
                                A real owner's story, written by Remi. Submitted with owner consent. Individual results vary.
                                {post.incentivized && (
                                    <>
                                        {" "}
                                        <em>Material connection: this owner earned listing credits for their submission.</em>
                                    </>
                                )}
                            </p>
                        </div>
                    </div>
                )}

                <div
                    className="mt-10 prose prose-invert max-w-none prose-p:text-onyx-300 prose-p:leading-relaxed prose-headings:font-serif prose-headings:text-white prose-a:text-hellcat prose-strong:text-white"
                    dangerouslySetInnerHTML={{ __html: post.html || `<p>${post.body || ""}</p>` }}
                />

                {post.bucket === "market_intel" && (
                    <div
                        className="mt-10 border-l-4 border-onyx-500 bg-onyx-800 p-4 brain-mono text-[11px] uppercase tracking-widest text-onyx-400"
                        data-testid="market-radar-disclaimer"
                    >
                        // MARKET RADAR DISCLAIMER — estimated market range based on available listings. Not a certified
                        appraisal. Prices vary by condition, mileage, and region.
                    </div>
                )}
            </motion.article>

            <footer className="border-t border-onyx-700 py-10 text-center label-caps">
                <Link to="/the-ladder" className="text-hellcat hover:underline">
                    ← Back to The Ladder
                </Link>
            </footer>
        </div>
    );
}
